import {
  Col,
  Form,
  Input,
  Row,
  Select,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import { useCallback, useEffect, useMemo } from "react";
import {
  buildDeductionTypeSelectOptions,
  DEDUCTION_TYPE_SELECT_CLASS,
  findLeafOptionByValue,
  formatDeductionTypeGroupLabel,
} from "./deductionTypeSelectOptions";
import { createReasonCodeKeyRules } from "./reasonCodeKeyValidation";

export interface ReasonCodeMappingAddEditFormValues {
  purchaserId: number | null;
  deductionTypeId: number | null;
  reasonCode: string;
  reasonCodeKey: string;
  promoTypeId: number | null;
}

export interface PurchaserSelectOption {
  purchaserId: number;
  purchaserName: string;
}

export interface DeductionTypeSelectGroup {
  typeId: number;
  typeName: string;
  subtypes: Array<{
    typeId: number;
    typeName: string;
  }>;
}

export interface PromoTypeSelectOption {
  promoTypeId: number;
  promoTypeName: string;
}

interface MasterDataReasonCodeMappingAddEditFormProps {
  form: FormInstance<ReasonCodeMappingAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: ReasonCodeMappingAddEditFormValues;
  purchaserOptions: PurchaserSelectOption[];
  purchasersLoading?: boolean;
  deductionTypeGroups: DeductionTypeSelectGroup[];
  deductionTypesLoading?: boolean;
  currentDeductionTypeOption?: { typeId: number; typeName: string } | null;
  promoTypeOptions: PromoTypeSelectOption[];
  promoTypesLoading?: boolean;
  currentPromoTypeOption?: PromoTypeSelectOption | null;
  onSubmit: (
    values: ReasonCodeMappingAddEditFormValues,
  ) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parseIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function findOptionByValue(
  options: SelectOption[],
  value: number | null,
): SelectOption | undefined {
  const raw = value == null ? null : String(value);
  return options.find((option) => option.value === raw);
}

function createRequiredIdRule(message: string) {
  return {
    validator: async (_: unknown, value: unknown) => {
      if (value == null || Number(value) <= 0) {
        throw new Error(message);
      }
    },
  };
}

interface IdSelectFieldProps {
  value?: number | null;
  onChange?: (next: number | null) => void;
  onBlur?: (...args: unknown[]) => void;
  options: SelectOption[];
  isLoading?: boolean;
  selectKey: string;
  placeholder: string;
  ariaLabel: string;
  isClearable?: boolean;
  className?: string;
  customGroupLabel?: (group: { label?: string }) => string;
  resolveSelected?: (
    options: SelectOption[],
    value: number | null,
  ) => SelectOption | undefined;
}

const IdSelectField = ({
  value,
  onChange,
  onBlur,
  options,
  isLoading = false,
  selectKey,
  placeholder,
  ariaLabel,
  isClearable = false,
  className,
  customGroupLabel,
  resolveSelected = findOptionByValue,
}: IdSelectFieldProps) => {
  const selected = resolveSelected(options, value ?? null);
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      className={className}
      size="medium"
      isSearchable
      isClearable={isClearable}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
      customGroupLabel={customGroupLabel}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseIdFromSelect(next))}
      onMenuClose={() => {
        if (!isLoading) {
          onBlur?.();
        }
      }}
      aria-label={ariaLabel}
    />
  );
};

export const MasterDataReasonCodeMappingAddEditForm = ({
  form,
  mode,
  initialValues,
  purchaserOptions,
  purchasersLoading = false,
  deductionTypeGroups,
  deductionTypesLoading = false,
  currentDeductionTypeOption = null,
  promoTypeOptions,
  promoTypesLoading = false,
  currentPromoTypeOption = null,
  onSubmit,
  onFormValidityChange,
}: MasterDataReasonCodeMappingAddEditFormProps) => {
  const keyDisabled = mode === "edit";

  const purchaserSelectOptions = useMemo<SelectOption[]>(
    () =>
      purchaserOptions.map((option) => ({
        label: option.purchaserName,
        value: String(option.purchaserId),
      })),
    [purchaserOptions],
  );

  const deductionTypeSelectOptions = useMemo(
    () =>
      buildDeductionTypeSelectOptions(
        deductionTypeGroups,
        currentDeductionTypeOption,
      ),
    [currentDeductionTypeOption, deductionTypeGroups],
  );

  const promoTypeSelectOptions = useMemo<SelectOption[]>(() => {
    const options = promoTypeOptions.map((option) => ({
      label: option.promoTypeName,
      value: String(option.promoTypeId),
    }));
    if (
      currentPromoTypeOption &&
      !options.some(
        (option) => option.value === String(currentPromoTypeOption.promoTypeId),
      )
    ) {
      options.unshift({
        label: currentPromoTypeOption.promoTypeName,
        value: String(currentPromoTypeOption.promoTypeId),
      });
    }
    return options;
  }, [currentPromoTypeOption, promoTypeOptions]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const purchaserId = values.purchaserId ?? null;
    const deductionTypeId = values.deductionTypeId ?? null;
    const reasonCode = values.reasonCode?.trim() ?? "";
    const reasonCodeKey = values.reasonCodeKey?.trim() ?? "";
    const promoTypeId = values.promoTypeId ?? null;

    const hasRequiredValues =
      purchaserId != null &&
      deductionTypeId != null &&
      reasonCode !== "" &&
      (mode === "edit" || reasonCodeKey !== "");

    const isDirty =
      mode === "create"
        ? true
        : purchaserId !== (initialValues.purchaserId ?? null) ||
          deductionTypeId !== (initialValues.deductionTypeId ?? null) ||
          reasonCode !== (initialValues.reasonCode?.trim() ?? "") ||
          promoTypeId !== (initialValues.promoTypeId ?? null);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const reasonCodeRules = useMemo(
    () => [
      { required: true, message: "Reason code is required" },
      {
        max: 255,
        message: "Reason code must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const reasonCodeKeyRules = useMemo(() => createReasonCodeKeyRules(), []);

  const handleReasonCodeChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      reasonCode: getInputValue(valueOrEvent),
    });
    form.validateFields(["reasonCode"]).catch(() => {});
    updateFormValidity();
  };

  const handleReasonCodeKeyChange = () => {
    if (keyDisabled) return;
    form.validateFields(["reasonCodeKey"]).catch(() => {});
    updateFormValidity();
  };

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-reason-code-mapping-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="reasonCode"
              label="Reason Code"
              validateTrigger={["onChange", "onBlur"]}
              rules={reasonCodeRules}
            >
              <Input
                placeholder="Reason code"
                onChange={handleReasonCodeChange}
                onInput={handleReasonCodeChange}
                onKeyUp={handleReasonCodeChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="reasonCodeKey"
              label="Reason Code Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={keyDisabled ? undefined : reasonCodeKeyRules}
              helpText={
                keyDisabled
                  ? "Once key is generated, it cannot be changed."
                  : undefined
              }
            >
              <Input
                placeholder="REASON-CODE-KEY"
                isDisabled={keyDisabled}
                onChange={handleReasonCodeKeyChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Form.Item
              name="purchaserId"
              label="Purchaser"
              validateTrigger={["onChange", "onBlur"]}
              rules={[createRequiredIdRule("Purchaser is required")]}
            >
              <IdSelectField
                options={purchaserSelectOptions}
                isLoading={purchasersLoading}
                selectKey={`${mode}-purchaser-${initialValues.purchaserId ?? "none"}`}
                placeholder="Select purchaser"
                ariaLabel="Purchaser"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="deductionTypeId"
              label="Deduction Type"
              validateTrigger={["onChange", "onBlur"]}
              rules={[createRequiredIdRule("Deduction type is required")]}
            >
              <IdSelectField
                options={deductionTypeSelectOptions}
                isLoading={deductionTypesLoading}
                selectKey={`${mode}-type-${initialValues.deductionTypeId ?? "none"}`}
                placeholder="Select deduction type"
                ariaLabel="Deduction type"
                className={DEDUCTION_TYPE_SELECT_CLASS}
                customGroupLabel={formatDeductionTypeGroupLabel}
                resolveSelected={findLeafOptionByValue}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Form.Item name="promoTypeId" label="Promo Type">
              <IdSelectField
                options={promoTypeSelectOptions}
                isLoading={promoTypesLoading}
                isClearable
                selectKey={`${mode}-promo-${initialValues.promoTypeId ?? "none"}`}
                placeholder="Select promo type"
                ariaLabel="Promo type"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
